import { AuthService } from './services/auth.service';
import {
   HttpEvent,
   HttpHandler,
   HttpRequest,
   HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, retry, tap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { environment } from '../environments/environment';
import { SessionService } from './services/session.service';
/**
 * Interceptor used for error handling
 */
@Injectable()
export class ErrorInterceptorService {
   /**
    * Class Constructor
    * @param toastrService
    */
   constructor(
      private toastrService: ToastrService,
      private authService: AuthService,
      private router:Router,
      public translate: TranslateService,
      private spinnerService:NgxSpinnerService,
      private sessionService: SessionService
   ) {}

   private isAccessRelatedRequest(request: HttpRequest<any>): boolean {
      return request.url.includes('external-sessions') || request.url.includes('sessionId=');
   }

   private resolveAccessInfoReason(reason: string): string {
      return this.sessionService.resolveAccessInfoReason(reason);
   }

   private redirectToAccessInfo(reason: string): void {
      this.spinnerService.hide();
      const baseUrl = environment.ioApplicationUrl || '/';
      const resolvedReason = this.resolveAccessInfoReason(reason);
      window.location.replace(`${baseUrl}notfound?reason=${encodeURIComponent(resolvedReason)}`);
   }

   private handleShareAccessError(
      request: HttpRequest<any>,
      response: HttpErrorResponse
   ): boolean {
      const message = response.error?.message;
      if (!message || !this.isAccessRelatedRequest(request)) {
         return false;
      }

      if (message.startsWith('This shared link expired on')) {
         this.redirectToAccessInfo(message);
         return true;
      }

      if (
         message === 'api_tokens.session_has_expired' &&
         request.url.includes('external-sessions/verify')
      ) {
         try {
            const sessionId = JSON.parse(request.body || '{}').sessionId;
            if (sessionId) {
               this.sessionService.getShareDetails(sessionId).subscribe({
                  next: (details) => {
                     this.redirectToAccessInfo(
                        this.sessionService.getExpiredShareAccessMessage(details.share)
                     );
                  },
                  error: () => {
                     this.redirectToAccessInfo(message);
                  }
               });
               return true;
            }
         } catch {
            // fall through to default redirect
         }
      }

      this.redirectToAccessInfo(message);
      return true;
   }

   intercept(
      request: HttpRequest<any>,
      next: HttpHandler
   ): Observable<HttpEvent<any>> {
      return next.handle(request).pipe(
         catchError((response: HttpErrorResponse) => {
            if (this.handleShareAccessError(request, response)) {
               return throwError(response.error?.message || response);
            }

            if (response.status === 401) {
               this.authService.logout();
            }
            if (response.status === 403 && !request.url.includes('s3.eu-central-1.amazonaws.com')) {
               this.router.navigate(['/notauthorized']);
            }
            let errorMessage = '';
            if (response.error instanceof ErrorEvent) {
               // client-side error
               if (response.error) {
                  if (response.error.message) {
                     errorMessage = response.error.message;
                     this.toastrService.error(errorMessage);
                     this.spinnerService.hide();

                  } else {
                     this.toastrService.error(this.translate.instant('notifications.something_went_wrong'));
                     this.spinnerService.hide();
                  }
               } else {
                  this.toastrService.error(this.translate.instant('notifications.something_went_wrong'));
                  this.spinnerService.hide();
               }
            } else {
               // server-side error
               if (!request.url.includes('s3.eu-central-1.amazonaws.com')) {
               if (this.handleShareAccessError(request, response)) {
               return throwError(response.error?.message || response);
               }
               if (response.status === 403) {
                  this.router.navigate(['/notauthorized']);
               }
               if (response.error) {
                  if (response.error.message) {
                     errorMessage = response.error.message;
                     this.toastrService.error(errorMessage);
                  } else {
                     this.toastrService.error(this.translate.instant('notifications.something_went_wrong'));
                     this.spinnerService.hide();
                  }
               } else if (response.status === 504) {
                  this.toastrService.error(this.translate.instant('notifications.we_are_experiencing_high_traffic_please_try_again_in_few_minutes_or_contact_us_for_help'));
                  this.spinnerService.hide();
               }  else {
                  this.toastrService.error(this.translate.instant('notifications.something_went_wrong'));
                  this.spinnerService.hide();
               }
            }
            }
            return throwError(errorMessage);
         })
      );
   }
}
